import React, { useRef, useState } from 'react';
import { HiCamera, HiPaperClip, HiX, HiExternalLink } from 'react-icons/hi';
import { MAX_FILE_SIZE_BYTES } from '@coparent/shared/src/utils/constants';
import toast from 'react-hot-toast';

const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/heic,image/webp,application/pdf';
const RECEIPT_TYPES = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf']);

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ReceiptUpload = ({
  existingReceipts = [],
  pendingFiles = [],
  onFileSelected,
  onRemovePending,
  onRemoveExisting,
  uploading,
  uploadProgress,
  disabled,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file) => {
    if (!RECEIPT_TYPES.has(file.type)) {
      toast.error('Only images and PDFs are accepted');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File must be under 10MB');
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      onFileSelected(file);
    }
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) {
      onFileSelected(file);
    }
  };

  const hasAny = existingReceipts.length > 0 || pendingFiles.length > 0;

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide">
        Receipt{hasAny ? 's' : ''} (optional)
      </label>

      {/* Existing receipts */}
      {existingReceipts.map((receipt, idx) => (
        <div key={receipt.storagePath || idx} className="flex items-center gap-2 rounded-xl border border-surface-200 p-3">
          <HiPaperClip className="text-canopy-500 text-sm flex-shrink-0" />
          <span className="text-xs text-ink-700 font-medium truncate flex-1">
            {receipt.fileName || 'Receipt'}
          </span>
          <a
            href={receipt.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-semibold text-canopy-600 hover:text-canopy-700 transition-colors"
          >
            <HiExternalLink className="text-sm" />
            View
          </a>
          {!disabled && onRemoveExisting && (
            <button
              type="button"
              onClick={() => onRemoveExisting(receipt)}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
            >
              <HiX className="text-sm" />
            </button>
          )}
        </div>
      ))}

      {/* Pending files */}
      {pendingFiles.map((file, idx) => (
        <div key={idx} className="flex items-center gap-2 rounded-xl border border-surface-200 p-3">
          <HiPaperClip className="text-canopy-500 text-sm flex-shrink-0" />
          <span className="text-xs text-ink-700 font-medium truncate flex-1">
            {file.name}
          </span>
          <span className="text-xs text-ink-400">{formatFileSize(file.size)}</span>
          <button
            type="button"
            onClick={() => onRemovePending(idx)}
            className="w-6 h-6 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
          >
            <HiX className="text-sm" />
          </button>
        </div>
      ))}

      {/* Upload progress */}
      {uploading && (
        <div className="rounded-xl border border-surface-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <HiPaperClip className="text-ink-400 text-sm" />
            <span className="text-xs text-ink-500 truncate flex-1">Uploading...</span>
            <span className="text-xs font-semibold text-canopy-600">{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-canopy-500 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Add receipt button / drop zone */}
      {!uploading && (
        <>
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`w-full rounded-xl border-2 border-dashed p-3 flex items-center justify-center gap-2 transition-colors ${
              isDragging
                ? 'border-canopy-400 bg-canopy-50'
                : 'border-surface-200 hover:border-canopy-300 hover:bg-surface-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <HiCamera className="text-ink-300 text-base" />
            <span className="text-xs text-ink-400 font-medium">
              {hasAny ? 'Add another receipt' : 'Attach receipt'}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={RECEIPT_ACCEPT}
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default ReceiptUpload;
